import traitCatalogJson from '../../assets/punksmarket-trait-catalog.json';

export interface PunksmarketTraitCatalogEntry {
  name: string;
  category?: string;
}

interface TraitCatalogFile {
  version: number;
  source: string;
  traitIdMax: number;
  traits: Record<string, PunksmarketTraitCatalogEntry>;
}

const catalog = traitCatalogJson as TraitCatalogFile;

const TRAIT_BY_ID = new Map<number, PunksmarketTraitCatalogEntry>(
  Object.entries(catalog.traits).map(([id, entry]) => [Number(id), entry]),
);

/** Display label for a PunksData filter trait id (0..110). */
export function getPunksmarketTraitLabel(traitId: number): string {
  const entry = TRAIT_BY_ID.get(traitId);
  if (entry?.name) {
    return entry.name;
  }
  return `Trait #${traitId}`;
}

/** Trait name with optional category and numeric id (detail lines). */
export function formatPunksmarketTraitRef(
  traitId: number,
  options: { includeNumericId?: boolean; includeCategory?: boolean } = {},
): string {
  const { includeNumericId = true, includeCategory = false } = options;
  const entry = TRAIT_BY_ID.get(traitId);
  const name = entry?.name ?? `Trait #${traitId}`;
  const parts = [name];
  if (includeCategory && entry?.category) {
    parts.push(`(${entry.category})`);
  }
  if (includeNumericId && entry?.name) {
    parts.push(`#${traitId}`);
  }
  return parts.join(' ');
}

export function formatTraitIdList(
  label: string,
  traitIds: number[],
  maxListed = 8,
): string {
  if (traitIds.length === 0) {
    return '';
  }
  const listed = traitIds
    .slice(0, maxListed)
    .map((id) =>
      formatPunksmarketTraitRef(id, {
        includeNumericId: true,
        includeCategory: true,
      }),
    )
    .join(', ');
  const suffix =
    traitIds.length > maxListed
      ? ` (+${traitIds.length - maxListed} more)`
      : '';
  return `${label}: ${listed}${suffix}`;
}

export interface PunksmarketTraitOption {
  id: number;
  name: string;
  category: string;
}

/** All filter trait options (0..traitIdMax), sorted by category then name. */
export function listPunksmarketTraitOptions(): PunksmarketTraitOption[] {
  const max = catalog.traitIdMax ?? 110;
  const options: PunksmarketTraitOption[] = [];
  for (let id = 0; id <= max; id++) {
    const entry = TRAIT_BY_ID.get(id);
    if (!entry?.name) {
      continue;
    }
    options.push({
      id,
      name: entry.name,
      category: entry.category ?? 'Other',
    });
  }
  return options.sort((a, b) => {
    const cat = a.category.localeCompare(b.category);
    return cat !== 0 ? cat : a.name.localeCompare(b.name);
  });
}

export function shortTraitListTag(prefix: string, traitIds: number[]): string | null {
  if (traitIds.length === 0) {
    return null;
  }
  if (traitIds.length === 1) {
    return `${prefix}: ${getPunksmarketTraitLabel(traitIds[0])}`;
  }
  if (traitIds.length <= 2) {
    return `${prefix}: ${traitIds.map(getPunksmarketTraitLabel).join(', ')}`;
  }
  return `${prefix} (${traitIds.length})`;
}

export const PUNKSMARKET_TRAIT_ID_MAX = catalog.traitIdMax ?? 110;

/** Slug form used by grid filters and PunksMarket trait names (e.g. "Green Eye Shadow" → greeneyeshadow). */
export function normalizeTraitNameToSlug(name: string): string {
  return name.trim().toLowerCase().replace(/[\s_\-]/g, '');
}

export interface PunksmarketTraitIndexEntry {
  id: number;
  slug: string;
  name: string;
  category: string;
}

/** On-chain filter trait index (ids 0..110) for placeBid criteria masks. */
export function listPunksmarketTraitIndexEntries(): PunksmarketTraitIndexEntry[] {
  const entries: PunksmarketTraitIndexEntry[] = [];
  for (let id = 0; id <= PUNKSMARKET_TRAIT_ID_MAX; id++) {
    const entry = TRAIT_BY_ID.get(id);
    if (!entry?.name) {
      continue;
    }
    entries.push({
      id,
      slug: normalizeTraitNameToSlug(entry.name),
      name: entry.name,
      category: entry.category ?? 'Other',
    });
  }
  return entries;
}
